import { useCallback, useEffect, useRef, useState } from 'react';
import { applyClientPricingDraftBulk, createClientPricingDraft, getClientPricingDraft, runPartialPricingBulk, updateClientPricingDraft } from '../api/bulkPricingApi';
import { getSelectedCompanyId } from '../utils/selectedCompany';

const canonical = (value) => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])])) : value;
const signature = (value) => JSON.stringify(canonical(value));

export default function useClientPricingBulkDraft({ enabled, userId, clientId, branchId, discounts, metadata, onRestore, onDraftChange }) {
  const key = `client-pricing-draft:${getSelectedCompanyId()}:${userId}:${clientId || 'new'}:${branchId}`;
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = useRef(null);
  const owner = useRef({ key });
  const callbacks = useRef({ onRestore, onDraftChange });
  callbacks.current = { onRestore, onDraftChange };
  const inputs = useRef({ discounts, metadata });
  inputs.current = { discounts, metadata };
  const queue = useRef(Promise.resolve());
  const restoring = useRef(null);
  const latest = signature({ discounts, metadata });
  const received = useCallback((value, restore = false) => {
    current.current = value;
    setDraft(value);
    callbacks.current.onDraftChange?.({ id: value.id, revision: value.revision });
    if (restore) {
      restoring.current = signature({ discounts: value.discounts, metadata: value.metadata });
      callbacks.current.onRestore?.(value);
    }
  }, []);
  useEffect(() => {
    let active = true;
    owner.current = { key };
    current.current = null;
    queue.current = Promise.resolve();
    restoring.current = null;
    setDraft(null);
    setBusy(false);
    setError('');
    callbacks.current.onDraftChange?.(null);
    if (!enabled || !branchId || !userId) return undefined;
    let id;
    try { id = localStorage.getItem(key); } catch { id = null; }
    if (!id) return undefined;
    setBusy(true);
    getClientPricingDraft(id).then((value) => { if (active) received(value, true); }).catch((err) => {
      if (!active) return;
      if (err?.response?.status === 404) { try { localStorage.removeItem(key); } catch { /* Storage disabled. */ } }
      else setError(err?.response?.data?.message || 'No se pudo recuperar el borrador.');
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [key, enabled, branchId, userId, received]);

  const save = useCallback(async () => {
    const savedKey = key;
    const ownerToken = owner.current;
    const desired = inputs.current;
    const action = async () => {
      if (owner.current !== ownerToken) throw new Error('El contexto del cliente cambió.');
      let value = current.current;
      if (!value) {
        value = await createClientPricingDraft({ clientId, branchId, ...desired });
        try { localStorage.setItem(savedKey, value.id); } catch { /* Server draft is available. */ }
      } else if (signature({ discounts: value.discounts, metadata: value.metadata }) !== signature(desired)) {
        value = await updateClientPricingDraft(value.id, { revision: value.revision, ...desired });
      }
      if (owner.current === ownerToken) received(value);
      return value;
    };
    queue.current = queue.current.catch(() => {}).then(action);
    return queue.current;
  }, [key, clientId, branchId, received]);
  useEffect(() => {
    if (!enabled || !draft || busy || error) return undefined;
    if (restoring.current) {
      if (restoring.current !== latest) return undefined;
      restoring.current = null;
    }
    if (signature({ discounts: draft.discounts, metadata: draft.metadata }) === latest) return undefined;
    const ownerToken = owner.current;
    const timer = setTimeout(() => {
      setBusy(true);
      save().then(() => { if (owner.current === ownerToken) setError(''); }).catch((err) => { if (owner.current === ownerToken) setError(err?.response?.data?.message || err.message); }).finally(() => { if (owner.current === ownerToken) setBusy(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [latest, draft, enabled, busy, error, save, key]);

  const applyBulk = async (payload) => {
    const ownerToken = owner.current;
    setBusy(true);
    setError('');
    try {
      const activeDraft = await save();
      if (owner.current !== ownerToken) throw new Error('El contexto del cliente cambió.');
      const result = await runPartialPricingBulk(`client-draft:${activeDraft.id}`, payload, (data) => applyClientPricingDraftBulk(activeDraft.id, data));
      if (owner.current === ownerToken) received(result.draft, true);
      return result;
    } catch (err) { if (owner.current === ownerToken) setError(err?.response?.data?.message || err.message); throw err; }
    finally { if (owner.current === ownerToken) setBusy(false); }
  };
  const reload = async () => {
    let id = current.current?.id;
    if (!id) { try { id = localStorage.getItem(key); } catch { /* Storage disabled. */ } }
    if (!id) return;
    const ownerToken = owner.current;
    setBusy(true);
    try {
      const value = await getClientPricingDraft(id);
      if (owner.current === ownerToken) { received(value, true); setError(''); }
    } catch (err) { if (owner.current === ownerToken) setError(err?.response?.data?.message || err.message); }
    finally { if (owner.current === ownerToken) setBusy(false); }
  };
  const retrySave = async () => {
    if (!current.current) {
      let storedId;
      try { storedId = localStorage.getItem(key); } catch { /* Storage disabled. */ }
      if (storedId) { await reload(); return; }
    }
    const ownerToken = owner.current;
    setBusy(true);
    try { await save(); if (owner.current === ownerToken) setError(''); }
    catch (err) { if (owner.current === ownerToken) setError(err?.response?.data?.message || err.message); }
    finally { if (owner.current === ownerToken) setBusy(false); }
  };
  const dirty = Boolean(draft && signature({ discounts: draft.discounts, metadata: draft.metadata }) !== latest);
  return { draft, busy, error, dirty, applyBulk, reload, retrySave };
}
